import { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ButtonDropdown,
  Container,
  Grid,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table
} from "@cloudscape-design/components";
import deploymentService from "services/deploymentService";
import { useNavigate, useParams } from "react-router-dom";
import { DeploymentItem } from "types/deploymentTypes";
import { getClusterStatusBadgeType, getPaymentInformationLabel } from "utils/Utils";
import DeleteConfirmationModal from "components/shared/DeleteConfirmationModal";
import { useNotification } from "contexts/NotificationContext";
import AppConfig from "configs/AppConfig";
import { useBreadcrumbs } from "contexts/BreadcrumbContext";
import { ExtensionObjectType } from "types/extensionTypes";
import { helpPanelDefaultContent, useHelpPanel } from "contexts/HelpPanelContext";
import ClusterExtensionLogs from "components/clusterExtensions/ClusterExtensionLogs";
import { useCluster } from "contexts/ClusterContext";





export default function DedicatedClusterDetailsPage() {
  
    const { clusterId } = useParams<{ clusterId: string }>();

    const [clusterObj, setClusterObj] = useState<DeploymentItem |null>(null);

    const [isLoadingCluster, setIsLoadingCluster] = useState(false);

    const [errorLoadingCluster, setErrorLoadingCluster] = useState<string|null>(null);

    const [isCancelingSubsctiption, setIsCancelingSubscription] = useState(false);
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const { addNotification } = useNotification();

    const { setBreadcrumbs } = useBreadcrumbs();

    const [extensionsList, setExtensionsList] = useState<ExtensionObjectType[]>([]);

    const [loadingExtensions, setLoadingExensions] = useState(false);

    const [errorLoadingExtensions, setErrorLoadingExtensions] = useState<string | null>(null);

    const { setIsHelpPanelOpen, setHelpPanelContent } = useHelpPanel();

    const [triggerRefreshLogs, setTriggerRefreshLogs] = useState(false); // Used to trigger logs refetch

    const { setSelectedCluster } = useCluster();

    const navigate = useNavigate();


    // Refresh help panel content when logs need to be reloaded
    useEffect(() => {
        if (!clusterId) return;

        setHelpPanelContent({
            header: "Logs",
            content: (
                <ClusterExtensionLogs
                    clusterId={clusterId}
                    triggerRefreshLogs={triggerRefreshLogs}
                />
            )
        });

    }, [triggerRefreshLogs]);



    // Initialize help panel content for the current cluster
    useEffect(()=>{
        if(!clusterId) return;
        
        setIsHelpPanelOpen(true);

        setHelpPanelContent({
            header: "Logs",
            content: <ClusterExtensionLogs clusterId={clusterId} triggerRefreshLogs={triggerRefreshLogs} />
        });

        return () => {
            setIsHelpPanelOpen(false);
            setHelpPanelContent(helpPanelDefaultContent);
        };
    },[clusterId]);




    useEffect(() => {
        if (clusterObj?.name) {
            setBreadcrumbs([
                { text: AppConfig.APP_NAME, href: "/" },
                { text: "Dedicated Clusters", href: "/dedicated-clusters" },
                { text: clusterObj.name, href: `/dedicated-clusters/${clusterObj?.id}` }
            ]);
        }

        // Cleanup when component unmounts
        return () => {
            setBreadcrumbs([]);
        };
    }, [clusterObj]);




    const fetchExtensions = async ()=>{
        try {
            setLoadingExensions(true);
            setErrorLoadingExtensions(null);

            const response = await deploymentService.getAllExtensions(clusterObj?.id, "all", "installed,pending");
            setExtensionsList(response);
        } catch (error) {
            setErrorLoadingExtensions("Failed to load extensions");
            console.error("Failed to Load Extensions", error);
        } finally{
            setLoadingExensions(false);
        }
    }

    useEffect(() => {
        if (!clusterObj?.id) return;

        fetchExtensions();
    }, [clusterObj?.id]);




    const fetchClusterById = async()=>{
        if(!clusterId) return;

        setIsLoadingCluster(true);
        setErrorLoadingCluster(null);

        try {
            const result = await deploymentService.fetchDedicatedClusterById(clusterId);
            setClusterObj(result);

        } catch (error) {
            console.error("Failed to get cluster with id ", clusterId);
            setErrorLoadingCluster("Failed to load cluster informations");
            console.error(error);

        } finally{
            setIsLoadingCluster(false);
        }
    }

    useEffect(()=>{
        fetchClusterById();
    },[])




    const cancelSubscriptionRequest = async ()=>{
        if(!clusterObj) return;
    
        setIsCancelingSubscription(true);
        try {
          await deploymentService.cancelSubscription(clusterObj?.id);
          addNotification("success", "Subscription canceled. The cluster will be inactive at the end of the billing period.");
          fetchClusterById();
    
        } catch (error) {
          console.error(error);
          addNotification("error", "Unable to cancel the subscription. Please try again.");
    
        } finally {
          setIsCancelingSubscription(false);
          setShowDeleteModal(false);
        }
    }



    const handleAddExtensionClick = ()=>{
        setSelectedCluster({label:clusterObj?.name, value:clusterObj?.id});
        navigate(`/dedicated-clusters/${clusterObj?.id}/extensions`);
    }


    
  
  
    if (isLoadingCluster) {
        return (
            <Box textAlign="center" padding={{ vertical: "xxl" }}>
                <SpaceBetween size="m">
                <Spinner size="large" />
                <Box color="text-body-secondary">Loading cluster details...</Box>
                </SpaceBetween>
            </Box>
        );
    }

    if (errorLoadingCluster) {
        return (
            <Box textAlign="center" padding={{ vertical: "xxl" }}>
                <SpaceBetween size="m">
                    <Box variant="h3" color="text-status-error">{errorLoadingCluster}</Box>
                    <Button 
                        onClick={fetchClusterById} 
                        iconName="refresh"
                    >
                        Retry
                    </Button>
                </SpaceBetween>
            </Box>
        );
    }

    if (!clusterObj) {
        return (
            <Box textAlign="center" padding={{ vertical: "xxl" }}>
                <SpaceBetween size="m">
                    <Box variant="h3">Cluster not found</Box>
                    <Box color="text-body-secondary">
                        The requested cluster does not exist.
                    </Box>
                </SpaceBetween>
            </Box>
        );
    }

    return (
        <SpaceBetween size="l">
            <Header
                variant="h2"
                info={
                    <div className="clustersHeader display-inline-block">
                        <Badge color={getClusterStatusBadgeType(clusterObj.status)}>
                            {clusterObj.status}
                        </Badge>

                        <Button 
                            variant="icon" 
                            iconName="refresh" 
                            onClick={async ()=>{
                                fetchClusterById();
                                fetchExtensions();
                                setTriggerRefreshLogs(prev => !prev);
                            }} 
                            disabled={isLoadingCluster} 
                        />
                    </div>
                }
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            iconName="external"
                            iconAlign="right"
                            onClick={() =>window.open(clusterObj?.console_url, "_blank")}
                            disabled = {clusterObj.status !== "active"}
                        >
                            Console
                        </Button>
                        <ButtonDropdown
                            items={[
                                {
                                id: "cancel-subscription",
                                text: "Cancel Subscription",
                                disabled: clusterObj.payment_status?.status === "canceled"
                                }
                            ]}
                            expandToViewport
                            variant="icon"
                            ariaLabel="Actions"
                            onItemClick={({ detail }) => {
                                const selected = detail.id;

                                switch (selected) {
                                case "cancel-subscription":
                                    setShowDeleteModal(true);
                                    break;

                                default:
                                    break;
                                }
                            }}
                        />
                    </SpaceBetween>
                }
            >
                {clusterObj.name}
            </Header>

        {/* Info Boxes Grid */}
            <Grid
                gridDefinition={[
                { colspan: { default: 12, xxs: 6 } },
                { colspan: { default: 12, xxs: 6 } }
                ]}
            >
                {/* Cluster details box */}
                <Container header={<Header variant="h3">Cluster details</Header>}>
                    <div className="flex flex-column gap-5">
                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Region</div>
                            <div>{clusterObj.region}</div>
                        </div>

                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Domain</div>
                            <div>{clusterObj.domainName}</div>
                        </div>
                        
                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Version</div>
                            <div>{clusterObj.version}</div>
                        </div>

                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Users</div>
                            <div>{clusterObj.users}</div>
                        </div>

                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Health</div>
                            --
                            {/* <StatusIndicator
                                type={clusterObj?.health ? "success" : "error"}
                            >
                                {clusterObj?.health ? "Healthy" : "Unhealthy"}
                            </StatusIndicator> */}
                        </div>
                    </div>
                </Container>

                {/* Subscription and plan box */}
                <Container header={<Header variant="h3">Subscription and plan</Header>}>
                    <div className="flex flex-column gap-5">
                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Plan</div>

                            <div>{getPaymentInformationLabel("plan", clusterObj?.payment_status?.plan)}</div>
                        </div>
                        
                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Size</div>

                            <div>{getPaymentInformationLabel("size", clusterObj?.payment_status?.size)}</div>
                        </div>

                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Billing Cycle</div>

                            <div>{getPaymentInformationLabel("billing_type", clusterObj?.payment_status?.billing_type)}</div>
                        </div>

                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Status</div>

                            <div>{getPaymentInformationLabel("status", clusterObj?.payment_status?.status)}</div>
                        </div>

                        <div className="flex justify-space-between gap-5">
                            <div className="bold gray-color">Created At</div>

                            <div>
                                {new Date(clusterObj.created_at).toLocaleDateString(undefined, {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric"
                                })}
                            </div>
                        </div>
                    </div>
                </Container>
            </Grid>

            <Table
                header={
                    <Header
                        variant="h3"
                        counter={`(${extensionsList.length})`}
                        info={
                            <Button
                                variant="icon"
                                iconName="refresh"
                                ariaLabel="Refresh extensions"
                                disabled={loadingExtensions}
                                onClick={() => {
                                    fetchExtensions();
                                    setTriggerRefreshLogs(prev => !prev);
                                }}
                            />
                        }
                        actions={
                            <SpaceBetween size="s" direction="horizontal">
                                <Button
                                    variant="normal"
                                    iconName="add-plus"
                                    onClick={handleAddExtensionClick}
                                >
                                    Extension
                                </Button>
                                <Button
                                    onClick={() => {
                                        setIsHelpPanelOpen(prev => !prev);
                                    }}
                                >
                                    View logs
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        Extensions
                    </Header>
                }
                items={extensionsList}
                loading={loadingExtensions}
                loadingText="Loading extensions..."
                columnDefinitions={[
                    {
                        id: "name",
                        header: "Name",
                        cell: item => item?.displayName,
                    },
                    {
                        id: "status",
                        header: "Status",
                        cell: item => (
                            <StatusIndicator
                                type={item.status === "installed" ? "success" : "pending"}
                            >
                                {item.status === "installed" ? "Installed" : "Pending"}
                            </StatusIndicator>
                        ),
                    },
                    {
                        id: "type",
                        header: "Type",
                        cell: item => !item.organization && !item.uploadedByUser ? "community" : "Custom",
                    },
                ]}
                empty={
                    errorLoadingExtensions ? (
                        <Box
                            margin={{ vertical: "xs" }}
                            textAlign="center"
                            color="inherit"
                        >
                            <SpaceBetween size="m">
                                <b>Unable to load extensions</b>

                                <Box variant="p" color="inherit">
                                    An error occurred while retrieving the installed extensions.
                                </Box>

                                <Button onClick={fetchExtensions}>
                                    Retry
                                </Button>
                            </SpaceBetween>
                        </Box>
                    ) : (
                        <Box
                            margin={{ vertical: "xs" }}
                            textAlign="center"
                            color="inherit"
                        >
                            <SpaceBetween size="m">
                                <b>No extensions installed</b>

                                <Box variant="p" color="inherit">
                                    This cluster doesn't have any installed extensions yet.
                                </Box>
                            </SpaceBetween>
                        </Box>
                    )
                }
            />

            <DeleteConfirmationModal 
                isVisible={showDeleteModal} 
                isDeleting={isCancelingSubsctiption} 
                headerText="Cancel Subscription"
                headerIconName="status-stopped"
                mainButtonText="Cancel Subscription"
                mainButtonLoadingText="Cancelling Subscription..."
                onDismiss={()=>{setShowDeleteModal(false)}} 
                onConfirm={cancelSubscriptionRequest} 
                deleteConfirmationValue={"cancel subscription"} 
                deleteAction="cancellation"
                showAlertBox={true}
                alertBoxTitle="Important Notice:"
                alertBoxContent={<>Your Cluster will remain active until the end of your current billing period. Once your legal subscription term ends, the associated cluster and its stored data will be permanently deleted in accordance with our <a target="_blank" href={`${AppConfig.ASWAR_DOCUMENTATION_URL}docs/compliance/data-deletion-exit-policy`}>Data Deletion & Exit Policy</a>. Please review the policy for more details.</>}
            >
                Are you sure you want to cancel your subscription for cluster: <b>{clusterObj?.name}</b> ?
            </DeleteConfirmationModal>
        </SpaceBetween>
    );
}